// DESTASH Restriction - Separates clearance/destash items at checkout
// Detects items with SKUs containing "DESTASH" (case-insensitive)

const DESTASH_KEYWORD = 'DESTASH';
const STORAGE_KEY = 'destash_saved_items';
let hasMixedCart = false;
let currentAnalysis = null;
let modal = null;

// ============ CART API ============

async function getCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

async function analyzeCart() {
  const cart = await getCart();

  const destashItems = cart.items.filter(i => i.sku?.toUpperCase().includes(DESTASH_KEYWORD));
  const regularItems = cart.items.filter(i => !i.sku?.toUpperCase().includes(DESTASH_KEYWORD));

  return {
    destashItems,
    regularItems,
    hasMixed: destashItems.length > 0 && regularItems.length > 0,
    isEmpty: cart.item_count === 0
  };
}

async function removeItemsFromCart(items) {
  const updates = {};
  items.forEach(item => { updates[item.key] = 0; });

  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates })
  });

  if (!response.ok) throw new Error('Failed to update cart');
  return response.json();
}

async function addItemsToCart(items) {
  const itemsToAdd = items.map(item => ({
    id: item.variant_id,
    quantity: item.quantity
  }));

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: itemsToAdd })
  });

  if (!response.ok) throw new Error('Failed to add items');
  return response.json();
}

// ============ LOCAL STORAGE ============

function saveItemsToStorage(items, type) {
  // Get existing saved items to merge with
  const existing = getSavedItems();

  const newItems = items.map(item => ({
    variant_id: item.variant_id,
    quantity: item.quantity,
    product_title: item.product_title,
    variant_title: item.variant_title,
    sku: item.sku
  }));

  let mergedItems = newItems;

  // Merge with existing items if they exist
  if (existing && existing.items && existing.items.length > 0) {
    const itemMap = new Map();

    // Add existing items to map
    existing.items.forEach(item => {
      itemMap.set(item.variant_id, { ...item });
    });

    // Merge new items (add quantities if same variant)
    newItems.forEach(item => {
      if (itemMap.has(item.variant_id)) {
        const existingItem = itemMap.get(item.variant_id);
        existingItem.quantity += item.quantity;
      } else {
        itemMap.set(item.variant_id, { ...item });
      }
    });

    mergedItems = Array.from(itemMap.values());
  }

  const data = {
    timestamp: new Date().toISOString(),
    type: type, // 'destash' or 'other' - use latest type
    items: mergedItems
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getSavedItems() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    const parsed = JSON.parse(data);
    // Expire after 7 days
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (new Date(parsed.timestamp).getTime() < sevenDaysAgo) {
      clearSavedItems();
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

function clearSavedItems() {
  localStorage.removeItem(STORAGE_KEY);
}

// ============ RESTORE BANNER ============

function updateRestoreBanner() {
  // Remove existing restore banner
  document.querySelectorAll('.destash-restore-banner').forEach(el => el.remove());

  const saved = getSavedItems();
  if (!saved || !saved.items || saved.items.length === 0) return;

  const itemCount = saved.items.length;

  const banner = document.createElement('div');
  banner.className = 'destash-restore-banner';
  banner.innerHTML = `
    <div class="destash-restore-banner__content">
      <span class="destash-restore-banner__icon">↻</span>
      <div class="destash-restore-banner__text">
        <strong>${itemCount} saved item${itemCount !== 1 ? 's' : ''}</strong>
        <span>From previous sessions</span>
      </div>
    </div>
    <button type="button" class="destash-restore-banner__btn" data-restore-cart>
      Restore
    </button>
  `;

  // Add click handler
  banner.querySelector('[data-restore-cart]').addEventListener('click', handleRestoreCart);

  // Insert into cart drawer
  const cartDrawer = document.querySelector('.cart-drawer');
  const cartContent = cartDrawer?.querySelector('.side-panel-content');
  if (cartContent) {
    cartContent.insertBefore(banner, cartContent.firstChild);
  }
}

async function handleRestoreCart() {
  const saved = getSavedItems();
  if (!saved || !saved.items) return;

  try {
    await addItemsToCart(saved.items);
    clearSavedItems();

    // Refresh cart UI
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Remove restore banner (items are now in cart)
    updateRestoreBanner();

    // Re-check cart state (may now have mixed cart)
    await checkCartState();
  } catch (error) {
    alert('Failed to restore items. Please try again.');
  }
}

// ============ WARNING BANNER (mixed cart) ============

function updateWarningBanner(show) {
  document.querySelectorAll('.destash-warning-banner').forEach(el => el.remove());
  if (!show) return;

  const banner = document.createElement('div');
  banner.className = 'destash-warning-banner';
  banner.innerHTML = `
    <span>Destash boxes must be purchased separately from regular items. You may add multiple destash boxes to the same order.</span>
  `;

  const cartDrawer = document.querySelector('.cart-drawer');
  const cartContent = cartDrawer?.querySelector('.side-panel-content');
  const restoreBanner = cartContent?.querySelector('.destash-restore-banner');

  if (cartContent) {
    // Insert after restore banner if it exists, otherwise at top
    if (restoreBanner) {
      restoreBanner.after(banner);
    } else {
      cartContent.insertBefore(banner, cartContent.firstChild);
    }
  }
}

// ============ MODAL ============

function createModal() {
  let existingModal = document.getElementById('DestashRestrictionModal');
  if (existingModal) return existingModal;

  const modalDiv = document.createElement('div');
  modalDiv.id = 'DestashRestrictionModal';
  modalDiv.className = 'destash-modal';
  modalDiv.setAttribute('aria-hidden', 'true');
  modalDiv.innerHTML = `
    <div class="destash-modal__overlay"></div>
    <div role="dialog" aria-modal="true" class="destash-modal__content" tabindex="-1">
      <button type="button" class="destash-modal__close" data-modal-close aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="destash-modal__body">
        <div class="destash-modal__icon">⚠️</div>
        <h3 class="destash-modal__title">Separate Checkout Required</h3>
        <p class="destash-modal__message">
          Destash items must be purchased separately from non-destash items. Please choose which items to check out with (the remaining items will be saved for later).
        </p>
        <div class="destash-modal__summary" data-summary></div>
        <div class="destash-modal__buttons">
          <button type="button" class="button full checkout" data-action="checkout-destash">
            <span data-destash-count data-content="Checkout Destash Only"></span>
          </button>
          <button type="button" class="button full outline" data-action="checkout-other">
            <span data-other-count data-content="Checkout Other Items"></span>
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modalDiv);
  return modalDiv;
}

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function getItemsTotal(items) {
  return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
}

function showModal(analysis) {
  modal = createModal();

  // Calculate total quantities (not unique line items)
  const destashQty = analysis.destashItems.reduce((sum, item) => sum + item.quantity, 0);
  const otherQty = analysis.regularItems.reduce((sum, item) => sum + item.quantity, 0);

  // Update button text with counts (only data-content, not textContent - CSS displays via pseudo-element)
  const destashBtn = modal.querySelector('[data-destash-count]');
  const otherBtn = modal.querySelector('[data-other-count]');
  const destashText = `Checkout Destash Only (${destashQty} item${destashQty !== 1 ? 's' : ''})`;
  const otherText = `Checkout Other Items (${otherQty} item${otherQty !== 1 ? 's' : ''})`;
  if (destashBtn) destashBtn.setAttribute('data-content', destashText);
  if (otherBtn) otherBtn.setAttribute('data-content', otherText);

  // Update summary
  const summary = modal.querySelector('[data-summary]');
  if (summary) {
    summary.innerHTML = `
      <div class="destash-modal__summary-row">
        <span>Destash (${destashQty})</span>
        <span>${formatPrice(getItemsTotal(analysis.destashItems))}</span>
      </div>
      <div class="destash-modal__summary-row">
        <span>Other items (${otherQty})</span>
        <span>${formatPrice(getItemsTotal(analysis.regularItems))}</span>
      </div>
    `;
  }

  // Setup handlers
  const checkoutDestashBtn = modal.querySelector('[data-action="checkout-destash"]');
  const checkoutOtherBtn = modal.querySelector('[data-action="checkout-other"]');
  const closeBtn = modal.querySelector('[data-modal-close]');

  checkoutDestashBtn?.replaceWith(checkoutDestashBtn.cloneNode(true));
  checkoutOtherBtn?.replaceWith(checkoutOtherBtn.cloneNode(true));
  closeBtn?.replaceWith(closeBtn.cloneNode(true));

  modal.querySelector('[data-action="checkout-destash"]')?.addEventListener('click', handleCheckoutWithDestash);
  modal.querySelector('[data-action="checkout-other"]')?.addEventListener('click', handleCheckoutWithOther);
  modal.querySelector('[data-modal-close]')?.addEventListener('click', hideModal);
  modal.querySelector('.destash-modal__overlay')?.addEventListener('click', hideModal);

  modal.classList.add('active');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');
}

function hideModal() {
  if (!modal) return;
  modal.classList.remove('active');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
}

// ============ CHECKOUT HANDLERS ============

async function handleCheckoutWithDestash() {
  if (currentAnalysis.regularItems.length > 0) {
    saveItemsToStorage(currentAnalysis.regularItems, 'other');
    await removeItemsFromCart(currentAnalysis.regularItems);
  }
  hideModal();
  window.location.href = '/checkout';
}

async function handleCheckoutWithOther() {
  if (currentAnalysis.destashItems.length > 0) {
    saveItemsToStorage(currentAnalysis.destashItems, 'destash');
    await removeItemsFromCart(currentAnalysis.destashItems);
  }
  hideModal();
  window.location.href = '/checkout';
}

// ============ MAIN STATE CHECK ============

async function checkCartState() {
  currentAnalysis = await analyzeCart();
  hasMixedCart = currentAnalysis.hasMixed;

  // Update UI
  updateRestoreBanner();
  updateWarningBanner(hasMixedCart);

  return currentAnalysis;
}

// ============ STYLES ============

function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    /* Restore Banner - Refined Design */
    .destash-restore-banner {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      margin: 0 0 16px;
      background: #FAF8F5;
      border: 1px solid #D4E5D7;
      border-radius: var(--input-border-radius, 4px);
      font-size: 13px;
    }
    .destash-restore-banner__content {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }
    .destash-restore-banner__icon {
      font-size: 16px;
      opacity: 0.8;
    }
    .destash-restore-banner__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .destash-restore-banner__text strong {
      color: var(--color-heading, #111);
      font-weight: 600;
    }
    .destash-restore-banner__text span {
      color: var(--color-body, #666);
      font-size: 12px;
    }
    .destash-restore-banner__btn {
      padding: 10px 16px;
      background: var(--solid-button-background, #111);
      color: var(--solid-button-label, white);
      border: none;
      border-radius: var(--button-border-radius, 4px);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }
    .destash-restore-banner__btn:hover {
      opacity: 0.9;
    }

    /* Warning Banner - Refined Design */
    .destash-warning-banner {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      margin: 0 0 16px;
      background: #FAF8F5;
      border: 1px solid var(--color-border, #E8E4DF);
      border-radius: var(--input-border-radius, 4px);
      color: var(--color-body, #232323);
      font-size: 13px;
      line-height: 1.5;
    }
    .destash-warning-banner__icon {
      font-size: 14px;
      opacity: 0.7;
    }

    /* Modal - Refined Design */
    .destash-modal {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0.3s ease;
    }
    .destash-modal.active { opacity: 1; visibility: visible; }
    .destash-modal__overlay {
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.4);
      backdrop-filter: blur(2px);
    }
    .destash-modal__content {
      position: relative;
      background: var(--bg-body, #fff);
      border-radius: var(--button-border-radius, 4px);
      width: 100%;
      max-width: 380px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      transform: translateY(8px);
      transition: transform 0.3s ease;
    }
    .destash-modal.active .destash-modal__content { transform: translateY(0); }
    .destash-modal__close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: var(--button-border-radius, 4px);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-body, #666);
      transition: background 0.2s ease;
    }
    .destash-modal__close:hover { background: rgba(0,0,0,0.05); }
    .destash-modal__body { padding: 32px 28px 28px; text-align: center; }
    .destash-modal__icon { display: none; }
    .destash-modal__title {
      margin: 0 0 12px;
      font-size: 22px;
      font-weight: 200;
      font-family: "PP Editorial New", serif;
      color: var(--color-heading, #000);
      letter-spacing: -0.01em;
    }
    .destash-modal__message {
      margin: 0 0 24px;
      font-size: 14px;
      line-height: 1.6;
      color: var(--color-body, #666);
    }
    .destash-modal__summary {
      background: #FAF8F5;
      border-radius: var(--input-border-radius, 4px);
      padding: 14px 16px;
      margin-bottom: 24px;
      font-size: 13px;
      color: var(--color-body, #444);
      text-align: left;
    }
    .destash-modal__summary-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
    }
    .destash-modal__summary-row + .destash-modal__summary-row {
      border-top: 1px solid var(--color-border, #E8E4DF);
      margin-top: 6px;
      padding-top: 12px;
    }
    .destash-modal__buttons { display: flex; flex-direction: column; gap: 12px; }
    .destash-modal__buttons .button { width: 100%; justify-content: center; }
    body.modal-open { overflow: hidden; }
  `;
  document.head.appendChild(style);
}

// ============ INITIALIZATION ============

document.addEventListener('DOMContentLoaded', () => {
  injectStyles();
  checkCartState();

  // Intercept checkout clicks
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[name="checkout"], .checkout-button');
    if (btn && hasMixedCart) {
      e.preventDefault();
      e.stopPropagation();
      showModal(currentAnalysis);
    }
  }, true);

  // Escape to close modal
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal?.classList.contains('active')) {
      hideModal();
    }
  });
});

// Re-check on cart changes
document.addEventListener('cart:item-added', () => checkCartState());
document.addEventListener('line-item:change:end', () => checkCartState());
document.addEventListener('cart:refresh', () => checkCartState());

// Expose for testing
window.DestashRestriction = {
  DESTASH_KEYWORD,
  getCart,
  analyzeCart,
  checkCartState,
  getSavedItems,
  clearSavedItems,
  showModal: () => showModal(currentAnalysis),
  hideModal,
  get hasMixedCart() { return hasMixedCart; }
};
